'use client';

import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'AVGS beantragen',
    desc: 'Besuche deine örtliche Arbeitsagentur oder dein Jobcenter und teile dein Interesse an einer Gründungs-Coaching-Maßnahme mit. Das Gespräch dauert meist unter 30 Minuten.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Ikiguide kontaktieren',
    desc: 'Du meldest dich bei uns. Wir prüfen gemeinsam, ob dein AVGS die Maßnahme abdeckt, und bestätigen dir die Teilnahme schriftlich.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Coaching startet – kostenlos',
    desc: 'Die Arbeitsagentur prüft und bewilligt. Du erhältst den schriftlichen Bescheid und das Gründungs-Coaching beginnt. Für dich entstehen keinerlei Kosten.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
];

export default function FundingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="foerderung" className="py-28 px-6 bg-charcoal text-white overflow-hidden relative">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cpath d='M0 0h60v60H0z' fill='none'/%3E%3Ccircle cx='30' cy='30' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
        backgroundSize: '30px 30px'
      }} />
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-sage/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20 reveal-hidden">
          <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest mb-5 bg-sage/15 text-sage-light border border-sage/25">
            <span className="w-1.5 h-1.5 rounded-full bg-sage-light inline-block" />
            Staatliche Förderung
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight max-w-3xl mx-auto">
            Kostenfreie Gründungsbegleitung,{' '}
            <span className="italic text-sage-light">wenn du arbeitssuchend bist.</span>
            <br />
            <span className="text-sand-light font-normal">100% staatlich gefördert.</span>
          </h2>
          <p className="mt-5 text-white/60 text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Der AVGS (Aktivierungs- und Vermittlungsgutschein) ermöglicht dir professionelles
            Gründungs-Coaching ohne einen einzigen Euro aus eigener Tasche.
          </p>
        </div>

        {/* AVGS / QCG cards */}
        <div className="flex justify-center mb-20">
          {/* AVGS Card */}
          <div className="reveal-hidden-left rounded-3xl p-8 border border-white/10 bg-white/[0.04] backdrop-blur-sm max-w-2xl w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A8C5AA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-white text-base">AVGS</h3>
                <p className="text-xs text-white/50">Für Arbeitssuchende</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-sage/20 text-sage-light px-3 py-1 rounded-full border border-sage/25">
                100% kostenlos
              </span>
            </div>
            <p className="text-white/65 text-sm leading-relaxed font-light mb-4">
              Der <strong className="text-white font-semibold">Aktivierungs- und Vermittlungsgutschein (AVGS)</strong> wird
              von der Agentur für Arbeit oder dem Jobcenter ausgestellt. Die Bundesagentur übernimmt
              vollständig die Kosten für dein Gründungs-Coaching.
            </p>
            <p className="text-white/45 text-xs font-light">
              Rechtsgrundlage: § 45 SGB III · AZAV-zertifizierter Träger
            </p>
          </div>
        </div>

        {/* 3 Steps */}
        <div className="reveal-hidden">
          <h3 className="text-center text-lg font-semibold text-white mb-10 uppercase tracking-widest text-sm">
            So einfach funktioniert es
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Connector lines (desktop) */}
            <div className="hidden md:block absolute top-12 left-[33%] right-[33%] h-px bg-gradient-to-r from-sage/30 via-sage/60 to-sage/30" />

            {steps?.map((step, i) => (
              <div
                key={i}
                className={`reveal-hidden delay-${(i + 1) * 100} flex flex-col items-center text-center p-7 rounded-3xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300`}
              >
                <div className="w-14 h-14 rounded-full bg-sage/15 border border-sage/30 flex items-center justify-center text-sage-light mb-4 relative z-10">
                  {step?.icon}
                </div>
                <span className="text-xs font-black text-sage-light/50 uppercase tracking-widest mb-1">
                  Schritt {step?.number}
                </span>
                <h4 className="font-bold text-white text-base mb-3">{step?.title}</h4>
                <p className="text-white/55 text-sm leading-relaxed font-light">{step?.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust footer */}
        <div className="mt-16 text-center reveal-hidden delay-300">
          <p className="text-white/40 text-sm font-light">
            AVGS ist ein offizielles Förderinstrument der Bundesagentur für Arbeit.
            Gesetzlich verankert. Für dich ohne Risiko.
          </p>
        </div>
      </div>
    </section>
  );
}